import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { AccountTransactionDetailsItem } from '@/components/molecules/AccountTransactionDetailsItem';
import { AmountTransactionDetailsItem } from '@/components/molecules/AmountTransactionDetailsItem';
import { RatesTransactionDetailsItem } from '@/components/molecules/RatesTransactionDetailsItem';
import { DetailsDrawerFees } from '@/components/organisms/DetailsDrawerFees';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { BitsoBank } from '@/types/bitsoBank';
import { PaymentRailType } from '@/types/currency';
import { DetailsField } from '@/types/detailsField';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import {
  FeeType,
  SimplifiedTransactionStatus,
  simplifyTransactionStatus,
  SpreadFee,
  StatusType,
  Transaction,
} from '@/types/transaction';
import { formatAmount } from '@/utility/formatAmount';

export const useTransactionDetailsFields = (
  transaction: Transaction,
  fromBanks: readonly BitsoBank[],
  toBanks: readonly BitsoBank[],
): ReadonlyArray<DetailsField | null> => {
  const { transactionDetails, date } = transaction;

  const locale = React.useContext<Locale>(LocaleContext);
  const dateFormatter = useDateFormatter('MMM d, yyyy | hh:mm a');
  const tr = useTranslator();
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  // TODO: replace this when the endpoint gets fixed
  const fixedDateString = React.useMemo((): string => `${date.replace(' ', 'T')}Z`, [date]);

  return React.useMemo(
    (): ReadonlyArray<DetailsField | null> =>
      transaction
        ? [
            {
              key: 'type',
              label: tr('TRANSACTION_TYPE'),
              value:
                tr(transaction.type) +
                (emptyPaymentTypes.includes(transaction.paymentType)
                  ? ''
                  : `: ${tr(transaction.paymentType)}`),
            },
            {
              key: 'date',
              label: tr('DATE_AND_TIME'),
              value: dateFormatter.parseAndFormat(fixedDateString),
            },
            transaction.fromAccount
              ? {
                  key: 'from',
                  label: tr('FROM'),
                  value: (
                    <AccountTransactionDetailsItem
                      account={transaction.fromAccount}
                      banks={fromBanks}
                    />
                  ),
                }
              : null,
            transaction.toRecipient || transaction.toAccount
              ? {
                  key: 'to',
                  label: tr('TO'),
                  value: (
                    <AccountTransactionDetailsItem
                      account={transaction.toRecipient ?? transaction.toAccount}
                      banks={toBanks}
                    />
                  ),
                }
              : null,
            {
              key: 'amount',
              label: tr('AMOUNT'),
              value: (
                <AmountTransactionDetailsItem
                  from={transaction.fromAmount}
                  to={transaction.toAmount}
                />
              ),
            },
            {
              key: 'fees',
              label: tr('FEES'),
              value: (
                <DetailsDrawerFees
                  totalAmount={transaction.fromAmount}
                  feeDetails={transaction.transactionDetails.feeDetails}
                  fromCurrency={transaction.fromAmount.currencyCode}
                />
              ),
            },
            {
              key: 'rates',
              label: tr('CONVERSION_RATES'),
              value: (
                <RatesTransactionDetailsItem
                  from={transaction.fromAmount}
                  to={transaction.toAmount}
                  conversionDetails={transaction.transactionDetails.conversionDetails}
                  spreadFees={transaction.transactionDetails.feeDetails.spreadFees}
                />
              ),
              columnSpan: 2,
            },
            getRevenueField(
              transactionDetails.feeDetails.spreadFees,
              locale,
              percentFormatter,
              tr,
              displayUsdcAsUsd,
              transaction.status,
              transaction.completed,
            ),
            transactionDetails.paymentReference
              ? {
                  key: 'paymentReference',
                  label: tr('PAYMENT_REFERENCE_DETAILS_LABEL'),
                  value: transactionDetails.paymentReference,
                  title: transactionDetails.paymentReference,
                  columnSpan: 2,
                }
              : null,
            {
              key: 'id',
              label: tr('TRANSACTION_ID'),
              value: transaction.id,
              title: transaction.id,
              columnSpan: 2,
            },
          ]
        : [],
    [
      dateFormatter,
      displayUsdcAsUsd,
      fixedDateString,
      fromBanks,
      locale,
      percentFormatter,
      toBanks,
      tr,
      transaction,
      transactionDetails.feeDetails.spreadFees,
      transactionDetails.paymentReference,
    ],
  );
};

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

const getRevenueField = (
  spreadFees: ReadonlyArray<SpreadFee>,
  locale: Locale,
  percentFormatter: Intl.NumberFormat,
  tr: (key: string, ...args: string[]) => string,
  displayUsdcAsUsd: boolean,
  status: StatusType,
  completed: boolean,
): DetailsField | null => {
  const simplified = simplifyTransactionStatus(status, completed);
  if (
    simplified === SimplifiedTransactionStatus.FAILED ||
    simplified === SimplifiedTransactionStatus.EXPIRED
  ) {
    return null;
  }

  const integratorSpread = spreadFees.find(
    (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
  );

  if (!integratorSpread) {
    return null;
  }

  const amount = formatAmount(
    locale,
    Number(integratorSpread.calculatedFee),
    integratorSpread.feeCurrency,
    undefined,
    undefined,
    displayUsdcAsUsd,
  );
  const percentage = percentFormatter.format(Number(integratorSpread.spreadBasisPoint));

  return {
    key: 'revenue',
    label: tr('YOUR_EARNINGS'),
    value: (
      <Box>
        <Typography variant="body2" color="text.primary">
          {amount}
        </Typography>
        <Typography variant="body2" color="custom.LightGray">
          {percentage} spread
        </Typography>
      </Box>
    ),
  };
};
